import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Link, useParams } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import WarehouseLayout from '../WarehouseLayout'
import type { LoadingList } from '../types'
import { fmtDate } from '../types'
import { useAuth } from '../../../contexts/AuthContext'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useColumnPrefs } from '../useColumnPrefs'
import { SortableColHeader, ColumnToggleButton, computeAutoFitWidths } from '../ColDragHeader'
import { useCustomersByRole } from '../../../hooks/useCustomersByRole'
import CustomerCombobox from '../../../components/CustomerCombobox'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { usePagination } from '../../../hooks/usePagination'
import PaginationControls from '../../../components/PaginationControls'

function Spin() {
  return (
    <svg className="animate-spin w-3 h-3 inline text-blue-500" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  )
}

const cellCls = 'w-full border border-blue-400 rounded px-1.5 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white'

function InlineText({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.select() }, [])
  return <input ref={ref} className={cellCls} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)} onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}

function InlineNumber({ value, onCommit, onCancel }: { value: string; onCommit: (v: string) => void; onCancel: () => void }) {
  const [v, setV] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.select() }, [])
  return <input ref={ref} type="number" min={0} className={`${cellCls} w-20`} value={v} onChange={e => setV(e.target.value)}
    onBlur={() => onCommit(v)} onKeyDown={e => { if (e.key === 'Enter') onCommit(v); if (e.key === 'Escape') onCancel() }} />
}


// ─── Column definitions ───────────────────────────────────────────────────────

const ALL_COLS = [
  { id: 'file',      label: 'File'              },
  { id: 'job_ref',   label: 'Job Ref'           },
  { id: 'container', label: 'Container'         },
  { id: 'customer',  label: 'Customer'          },
  { id: 'uploaded',  label: 'Uploaded'          },
  { id: 'status',    label: 'Status'            },
  { id: 'ctns',      label: 'CTNS (exp/parsed)' },
] as const

const LL_AUTO_WIDTHS = computeAutoFitWidths(ALL_COLS)

interface DeleteInfo {
  ll: LoadingList
  planCount: number  // >0 means blocked
}

function storagePath(url: string): string | null {
  const marker = '/warehouse-files/'
  const idx = url.indexOf(marker)
  return idx >= 0 ? decodeURIComponent(url.slice(idx + marker.length)) : null
}

const STATUS_COLORS: Record<string, string> = {
  manual:  'bg-gray-100 text-gray-600',
  pending: 'bg-yellow-100 text-yellow-700',
  parsing: 'bg-blue-100 text-blue-700',
  parsed:  'bg-green-100 text-green-700',
  failed:  'bg-red-100 text-red-700',
}

function parsedTotals(ll: LoadingList) {
  const items = ll.parsed_data?.items || []
  const ctns = items.reduce((s, it) => s + (it.expected_cartons || 0), 0)
  const wt = items.reduce((s, it) => s + (it.expected_weight_kg || 0), 0)
  const cbm = items.reduce((s, it) => s + (it.expected_cbm || 0), 0)
  return { ctns, wt: wt || null, cbm: cbm || null }
}

function TotalCell({ expected, parsed }: { expected: number | null; parsed: number | null }) {
  if (expected == null && (parsed == null || parsed === 0)) return <span className="text-gray-400">—</span>
  if (expected == null) return <span className="text-gray-500">{parsed}</span>
  const match = parsed != null && Math.abs(parsed - expected) < 0.01
  return (
    <span className={match ? 'text-green-700 dark:text-green-400' : 'text-amber-600 dark:text-amber-400 font-medium'}>
      {expected} / {parsed ?? '?'}
      {!match && ' ⚠'}
    </span>
  )
}

export default function LoadingListsPage() {
  const { id } = useParams<{ id: string }>()
  const { user } = useAuth()
  const [lists, setLists] = useState<LoadingList[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [deleteInfo, setDeleteInfo] = useState<DeleteInfo | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Inline edit
  type EditField = 'container_no' | 'source_customer_id' | 'expected_total_cartons'
  const [editCell, setEditCell] = useState<{ llId: string; field: EditField; value: string } | null>(null)
  const [savingCell, setSavingCell] = useState<string | null>(null)
  const { customers: customerOpts } = useCustomersByRole('Cus')

  const saveCell = useCallback(async (ll: LoadingList, field: EditField, rawValue: string) => {
    const key = `${ll.id}:${field}`
    setSavingCell(key)
    setEditCell(null)
    try {
      const patchVal = field === 'expected_total_cartons'
        ? (parseInt(rawValue) || null)
        : (rawValue || null)
      const { error } = await supabase.from('loading_lists').update({ [field]: patchVal }).eq('id', ll.id)
      if (error) throw error
      setLists(prev => prev.map(x => {
        if (x.id !== ll.id) return x
        if (field === 'container_no') return { ...x, container_no: rawValue || null }
        if (field === 'expected_total_cartons') return { ...x, expected_total_cartons: parseInt(rawValue) || null }
        if (field === 'source_customer_id') {
          const c = customerOpts.find(o => o.id === rawValue)
          return { ...x, source_customer_id: rawValue || null, customer: c ? { code: c.code, name: c.name } : null }
        }
        return x
      }))
    } catch (err: any) {
      alert(err.message)
    } finally {
      setSavingCell(null)
    }
  }, [customerOpts])

  // Column drag-reorder + show/hide (migrates from old localStorage keys)
  const { order: llColOrder, hidden: llHidden, visibleCols, setOrder: setLLOrder,
    toggleHidden: toggleLLCol, reset: resetLLCols,
    extra: llExtra, setExtra: setLlExtra } =
    useColumnPrefs('col_pref_loading_lists', ALL_COLS, user?.id, {
      lsHidden: 'll_index_hidden_cols',
      lsOrder:  'll_index_col_order',
    }, { widths: {} })
  const llSavedWidths = (llExtra.widths as Record<string, number>) ?? {}
  const getLlColW = (id: string) => llSavedWidths[id] ?? LL_AUTO_WIDTHS[id] ?? 120
  const saveLlColW = (id: string, w: number) => setLlExtra({ ...llExtra, widths: { ...llSavedWidths, [id]: w } })
  const resetLlColW = (id: string) => { const n = { ...llSavedWidths }; delete n[id]; setLlExtra({ ...llExtra, widths: n }) }

  const llSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  const handleLLColDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oi = llColOrder.indexOf(String(active.id))
      const ni = llColOrder.indexOf(String(over.id))
      if (oi !== -1 && ni !== -1) setLLOrder(arrayMove(llColOrder, oi, ni))
    }
  }

  useEffect(() => {
    if (!id) return
    supabase.from('loading_lists')
      .select('*, customer:customers(code, name)')
      .eq('warehouse_id', id)
      .order('uploaded_at', { ascending: false })
      .then(({ data }) => { setLists((data as LoadingList[]) || []); setLoading(false) })
  }, [id])

  const handleDeleteClick = async (ll: LoadingList) => {
    const { data: plans } = await supabase.from('devanning_plans')
      .select('id').eq('source_loading_list_id', ll.id)
    const planIds = (plans || []).map((p: any) => p.id)
    setDeleteInfo({ ll, planCount: planIds.length })
  }

  const handleDeleteConfirm = async () => {
    if (!deleteInfo || deleteInfo.planCount > 0) return
    const { ll } = deleteInfo
    setDeleting(true)
    try {
      if (ll.file_url) {
        const path = storagePath(ll.file_url)
        if (path) await supabase.storage.from('warehouse-files').remove([path])
      }
      await supabase.from('loading_lists').delete().eq('id', ll.id)
      setLists(prev => prev.filter(x => x.id !== ll.id))
      setDeleteInfo(null)
    } finally {
      setDeleting(false)
    }
  }

  const filtered = useMemo(() => {
    if (!search.trim()) return lists
    const q = search.toLowerCase()
    return lists.filter(ll =>
      (ll.file_name || '').toLowerCase().includes(q) ||
      (ll.job_ref || '').toLowerCase().includes(q) ||
      (ll.container_no || '').toLowerCase().includes(q) ||
      (ll.customer?.name || '').toLowerCase().includes(q) ||
      (ll.customer?.code || '').toLowerCase().includes(q)
    )
  }, [lists, search])

  const { scrollProps: llScrollProps, contentRef: llTableRef, bar: llScrollBar } = useStickyHScroll([filtered, visibleCols])

  const { page, setPage, pageSize, setPageSize } = usePagination('loading-lists')
  useEffect(() => { setPage(0) }, [search]) // eslint-disable-line react-hooks/exhaustive-deps
  const pagedTotal = filtered.length
  const paged = pageSize === 'all' ? filtered : filtered.slice(page * pageSize, page * pageSize + pageSize)

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700 gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h2 className="font-semibold text-gray-900 dark:text-white">Loading Lists</h2>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search file, job ref, container, customer..."
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
              />
              <svg className="absolute left-2.5 top-2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ColumnToggleButton
              cols={ALL_COLS}
              hidden={llHidden}
              onToggle={id => toggleLLCol(id)}
              onReset={resetLLCols}
              onRecalculate={() => { const w = computeAutoFitWidths(ALL_COLS); setLlExtra({ ...llExtra, widths: { ...w, ...llSavedWidths } }) }}
            />
            <Link to={`/warehouse/${id}/loading-lists/upload`}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap">
              + Upload Loading List
            </Link>
          </div>
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-400">Loading...</div>
        ) : (
          <div className="overflow-x-auto" {...llScrollProps}>
            <table ref={llTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
              <DndContext sensors={llSensors} collisionDetection={closestCenter} onDragEnd={handleLLColDragEnd}>
                <SortableContext items={llColOrder} strategy={horizontalListSortingStrategy}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {visibleCols.map(col => (
                        <SortableColHeader key={col.id} id={col.id}
                          style={{ width: getLlColW(col.id), minWidth: '50px' }}
                          className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap"
                          onResizeEnd={w => saveLlColW(col.id, w)}
                          onResizeReset={() => resetLlColW(col.id)}>
                          {col.label}
                        </SortableColHeader>
                      ))}
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                </SortableContext>
              </DndContext>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {paged.map(ll => {
                  const pt = parsedTotals(ll)
                  return (
                    <tr key={ll.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {visibleCols.map(col => {
                        switch (col.id) {
                          case 'file':      return <td key="file" className="px-4 py-3 font-medium text-gray-900 dark:text-white">{ll.file_name || '(manual entry)'}</td>
                          case 'job_ref':   return <td key="job_ref" className="px-4 py-3 font-mono text-sm text-blue-700 dark:text-blue-400">{ll.job_ref || '—'}</td>
                          case 'container': {
                            const isEd = editCell?.llId === ll.id && editCell.field === 'container_no'
                            const isSv = savingCell === `${ll.id}:container_no`
                            return (
                              <td key="container" className="group px-4 py-3 cursor-text text-gray-500"
                                onClick={() => !isEd && !isSv && setEditCell({ llId: ll.id, field: 'container_no', value: ll.container_no || '' })}>
                                {isSv ? <Spin /> : isEd ? (
                                  <InlineText value={editCell!.value} onCommit={v => saveCell(ll, 'container_no', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1">{ll.container_no || '—'}
                                    <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>
                                  </span>
                                )}
                              </td>
                            )
                          }
                          case 'customer': {
                            const isEd = editCell?.llId === ll.id && editCell.field === 'source_customer_id'
                            const isSv = savingCell === `${ll.id}:source_customer_id`
                            return (
                              <td key="customer" className="group px-4 py-3 cursor-text text-gray-500"
                                onClick={() => !isEd && !isSv && setEditCell({ llId: ll.id, field: 'source_customer_id', value: (ll as any).source_customer_id || '' })}>
                                {isSv ? <Spin /> : isEd ? (
                                  <CustomerCombobox value={editCell!.value}
                                    onChange={v => saveCell(ll, 'source_customer_id', v)}
                                    onClose={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1">{ll.customer?.name || '—'}
                                    <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>
                                  </span>
                                )}
                              </td>
                            )
                          }
                          case 'uploaded':  return <td key="uploaded" className="px-4 py-3 text-gray-500">{fmtDate(ll.uploaded_at)}</td>
                          case 'status':    return <td key="status" className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[ll.parse_status] || ''}`}>{ll.parse_status}</span></td>
                          case 'ctns': {
                            const isEd = editCell?.llId === ll.id && editCell.field === 'expected_total_cartons'
                            const isSv = savingCell === `${ll.id}:expected_total_cartons`
                            return (
                              <td key="ctns" className="group px-4 py-3 cursor-text text-sm"
                                onClick={() => !isEd && !isSv && setEditCell({ llId: ll.id, field: 'expected_total_cartons', value: String(ll.expected_total_cartons ?? '') })}>
                                {isSv ? <Spin /> : isEd ? (
                                  <InlineNumber value={editCell!.value} onCommit={v => saveCell(ll, 'expected_total_cartons', v)} onCancel={() => setEditCell(null)} />
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <TotalCell expected={ll.expected_total_cartons} parsed={pt.ctns || null} />
                                    <svg className="w-3 h-3 text-gray-300 group-hover:text-gray-400 opacity-0 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536M9 13l6.5-6.5a2 2 0 012.828 2.828L11.828 15.828a2 2 0 01-1.414.586H8v-2.414a2 2 0 01.586-1.414z" /></svg>
                                  </span>
                                )}
                              </td>
                            )
                          }
                          default:          return null
                        }
                      })}
                      <td className="px-4 py-3 whitespace-nowrap space-x-3 text-xs">
                        <Link to={`/warehouse/${id}/loading-lists/${ll.id}`} className="text-blue-600 hover:underline">Open →</Link>
                        <button onClick={() => handleDeleteClick(ll)} className="text-red-500 hover:text-red-700 hover:underline">Delete</button>
                      </td>
                    </tr>
                  )
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={visibleCols.length + 1} className="px-4 py-12 text-center text-gray-400">
                    {search ? 'No results for this search.' : 'No loading lists yet — upload the first one.'}
                  </td></tr>
                )}

              </tbody>
            </table>
          </div>
        )}
        {!loading && filtered.length > 0 && (
          <PaginationControls page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} total={pagedTotal} />
        )}
      </div>
      <StickyHScrollbar {...llScrollBar} />
      {deleteInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            {deleteInfo.planCount > 0 ? (
              <>
                <h3 className="font-semibold text-amber-600 dark:text-amber-400 mb-3">⚠ Cannot Delete Loading List</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  &ldquo;<strong>{deleteInfo.ll.file_name || deleteInfo.ll.job_ref || '(manual entry)'}</strong>&rdquo;
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mb-4">
                  {deleteInfo.planCount} Pre-Devanning Plan{deleteInfo.planCount !== 1 ? 's' : ''} still exist{deleteInfo.planCount === 1 ? 's' : ''}.
                  Please delete {deleteInfo.planCount === 1 ? 'it' : 'them'} first, then come back.
                </p>
                <div className="flex justify-end">
                  <button onClick={() => setDeleteInfo(null)}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Confirm Delete Loading List</h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                  &ldquo;<strong>{deleteInfo.ll.file_name || deleteInfo.ll.job_ref || '(manual entry)'}</strong>&rdquo;
                </p>
                <p className="text-sm font-semibold text-red-600 mb-4">This will permanently remove the file and all parsed items.</p>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setDeleteInfo(null)} disabled={deleting}
                    className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={handleDeleteConfirm} disabled={deleting}
                    className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Confirm Delete'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
